import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { usePreferencesStore } from '@/stores/preferences';
import { isNativeApp } from '@/lib/native';
import { applySeo, type PageSeo } from '@/lib/seo';
import { GUIDES } from '@/content/guides';

declare module 'vue-router' {
  interface RouteMeta {
    /** Absent : page non indexée (espace connecté, liens à jeton). */
    seo?: PageSeo;
    guideSlug?: string;
  }
}

const router = createRouter({
  history: createWebHistory(),
  // Ancres de la page d'accueil (#faq…) et retour en haut à chaque page.
  scrollBehavior: (to, _from, saved) => saved ?? (to.hash ? { el: to.hash } : { top: 0 }),
  routes: [
    // Page d'accueil publique, visible connecté ou non (elle adapte ses boutons).
    {
      path: '/',
      name: 'landing',
      component: () => import('@/views/LandingView.vue'),
      meta: {
        public: true,
        landing: true,
        seo: { title: 'Yakafa — Facturation électronique et devis IA pour TPE' },
      },
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('@/views/VerifyEmailView.vue'),
      meta: { public: true, open: true },
    },
    {
      path: '/aide',
      name: 'help',
      component: () => import('@/views/HelpView.vue'),
      meta: {
        public: true,
        open: true,
        seo: {
          title: "Centre d'aide",
          description: "Aide Yakafa : créer son compte, statuts des factures, Peppol, IBAN, abonnement, résiliation. Et un formulaire pour contacter l'assistance.",
        },
      },
    },
    // Guides (SEO, GEO) : une route par guide, pour des balises propres à chacun.
    {
      path: '/guides',
      name: 'guides',
      component: () => import('@/views/guides/GuidesIndexView.vue'),
      meta: {
        public: true,
        open: true,
        seo: {
          title: 'Guides de la facturation électronique',
          description: 'Guides pratiques et sourcés pour les indépendants et TPE : réforme de la facturation électronique 2026, mentions obligatoires, Peppol, franchise de TVA.',
        },
      },
    },
    ...GUIDES.map((guide) => ({
      path: `/guides/${guide.slug}`,
      name: `guide-${guide.slug}`,
      component: () => import('@/views/guides/GuideView.vue'),
      meta: { public: true, open: true, guideSlug: guide.slug, seo: { title: guide.seoTitle, description: guide.seoDescription } },
    })),
    // Documents légaux : publics, consultables connecté ou non.
    { path: '/mentions-legales', name: 'legal-notice', component: () => import('@/views/legal/LegalNoticeView.vue'), meta: { public: true, open: true, seo: { title: 'Mentions légales', description: "Mentions légales de Yakafa : éditeur, hébergeur, contact." } } },
    { path: '/cgu', name: 'terms', component: () => import('@/views/legal/TermsView.vue'), meta: { public: true, open: true, seo: { title: "Conditions générales d'utilisation et de vente", description: "CGU et CGV de Yakafa : essai gratuit, abonnements, facturation électronique, responsabilités." } } },
    { path: '/confidentialite', name: 'privacy', component: () => import('@/views/legal/PrivacyView.vue'), meta: { public: true, open: true, seo: { title: 'Politique de confidentialité', description: "Données traitées par Yakafa, sous-traitants, durées de conservation et exercice de vos droits RGPD." } } },
    { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { public: true, seo: { title: 'Connexion', description: 'Connectez-vous à Yakafa ou créez votre compte : essai gratuit, sans carte bancaire.', noindex: true } } },
    { path: '/forgot-password', name: 'forgot-password', component: () => import('@/views/ForgotPasswordView.vue'), meta: { public: true } },
    // `open` : accessible même connecté (lien reçu par email, sur un appareil déjà connecté).
    // Réservation en ligne chez un artisan : publique, jamais indexée (pages propres à chaque artisan).
    { path: '/rdv/annuler/:token', name: 'booking-cancel', component: () => import('@/views/BookingCancelView.vue'), meta: { public: true, open: true } },
    { path: '/rdv/:slug', name: 'booking', component: () => import('@/views/BookingPublicView.vue'), meta: { public: true, open: true } },
    { path: '/iban-cancel', name: 'iban-cancel', component: () => import('@/views/IbanCancelView.vue'), meta: { public: true, open: true } },
    { path: '/reset-password', name: 'reset-password', component: () => import('@/views/ResetPasswordView.vue'), meta: { public: true, open: true } },
    // Tableau de bord (synthèse, accueil après connexion) et liste des factures, séparés.
    { path: '/dashboard', name: 'dashboard', component: () => import('@/views/DashboardView.vue') },
    { path: '/invoices', name: 'invoices', component: () => import('@/views/InvoicesView.vue') },
    { path: '/invoices/new', name: 'invoice-new', component: () => import('@/views/InvoiceEditorView.vue') },
    { path: '/invoices/:id', name: 'invoice-detail', component: () => import('@/views/InvoiceDetailView.vue') },
    { path: '/invoices/:id/edit', name: 'invoice-edit', component: () => import('@/views/InvoiceEditorView.vue') },
    { path: '/receipts', name: 'receipts', component: () => import('@/views/ReceiptsView.vue') },
    { path: '/expenses', name: 'expenses', component: () => import('@/views/ExpensesView.vue') },
    { path: '/bank', name: 'bank', component: () => import('@/views/BankView.vue') },
    { path: '/planning', name: 'planning', component: () => import('@/views/PlanningView.vue') },
    { path: '/accounting', name: 'accounting', component: () => import('@/views/AccountingView.vue') },
    { path: '/quotes', name: 'quotes', component: () => import('@/views/QuotesView.vue') },
    { path: '/quotes/new', name: 'quote-new', component: () => import('@/views/QuoteEditorView.vue') },
    { path: '/quotes/:id', name: 'quote-detail', component: () => import('@/views/QuoteDetailView.vue') },
    { path: '/quotes/:id/edit', name: 'quote-edit', component: () => import('@/views/QuoteEditorView.vue') },
    // Page du client qui accepte un devis : sans compte, ouverte même connecté.
    {
      path: '/q/:token',
      name: 'quote-public',
      component: () => import('@/views/QuotePublicView.vue'),
      meta: { public: true, open: true },
    },
    {
      path: '/invitation-comptable/:token',
      name: 'accountant-invite',
      component: () => import('@/views/AccountantInviteView.vue'),
      meta: { public: true, open: true },
    },
    { path: '/signatures', name: 'signatures', component: () => import('@/views/SignaturesView.vue') },
    { path: '/demandes/:id?', name: 'leads', component: () => import('@/views/LeadsView.vue') },
    { path: '/comptable', name: 'accountant', component: () => import('@/views/AccountantView.vue'), meta: { accountant: true } },
    { path: '/clients', name: 'clients', component: () => import('@/views/CompaniesView.vue') },
    // Ancienne adresse (« Entreprises ») : liens et favoris existants.
    { path: '/companies', redirect: '/clients' },
    {
      path: '/preferences',
      name: 'preferences',
      // Ouvert à tout utilisateur connecté, pas seulement aux administrateurs.
      component: () => import('@/views/PreferencesView.vue'),
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/views/AdminView.vue'),
      meta: { admin: true },
    },
    { path: '/:pathMatch(.*)*', redirect: '/invoices' },
  ],
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  // La session vit dans un cookie HTTP-only : seul le serveur peut nous dire
  // si elle est valide, d'où cet appel au premier routage.
  await auth.bootstrap();
  if (auth.user) await usePreferencesStore().bootstrap();

  // App : pas de page d'accueil marketing (ni prix, cf. règles de l'App Store).
  if (isNativeApp && to.meta.landing) return { name: auth.user ? 'dashboard' : 'login' };
  if (!to.meta.public && !auth.user) return { name: 'login', query: { redirect: to.fullPath } };
  if (to.meta.public && !to.meta.landing && !to.meta.open && auth.user) return { name: auth.user.kind === 'accountant' ? 'accountant' : 'dashboard' };
  // Expert-comptable : son seul espace (l'API lui ferme de toute façon le reste).
  const accountant = auth.user?.kind === 'accountant';
  if (accountant && !to.meta.public && !to.meta.accountant) return { name: 'accountant' };
  if (to.meta.accountant && !accountant) return { name: 'dashboard' };
  // Garde de confort : l'API refuse de toute façon un non-admin (403).
  if (to.meta.admin && !auth.isAdmin) return { name: 'dashboard' };
  return true;
});

router.afterEach((to) => applySeo(to.meta.seo, to.path));

export default router;
